import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { BarChartSquare02, Rows01, Users01, MessageChatCircle } from '@untitledui/icons';
import {
  fetchCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
  fetchCompanyDocuments,
  createCompanyDocument,
  deleteCompanyDocument,
  updateCompanyDocumentStatus,
  fetchUsers,
  createPocketbaseUser,
} from '../../services/adminApi';
import {
  COMPANY_DOCUMENT_STATUS,
  getCompanyDocumentFileUrl,
} from '../../services/companyDocuments';
import { COMPANY_DOCUMENT_MAP } from '../../constants/companyDocuments';
import { fetchPageVisitSummary, PAGE_VISIT_TIMEFRAMES } from '../../services/pageVisitsApi';
import { getPageVisitDefinitions } from '../../constants/pageVisits';
import OverviewTab from './components/OverviewTab';
import CompaniesTab from './components/CompaniesTab';
import UsersTab from './components/UsersTab';
import SupportTab from './components/SupportTab';
import AdminSidebar from './AdminSidebar';
import './AdminDashboard.css';
import '../dashboard/Dashboard.css';

const computeAverageDuration = (total, count) => {
  if (!count || count <= 0) return 0;
  const safeTotal = typeof total === 'number' && Number.isFinite(total) ? total : 0;
  return Math.round(Math.max(safeTotal, 0) / count);
};

const formatDuration = durationMs => {
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) {
    return '<1s';
  }
  const totalSeconds = Math.floor(durationMs / 1000);
  if (totalSeconds < 1) return '<1s';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    if (minutes > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
};

const blankCompany = (currentUserId = '') => ({
  name: '',
  slug: '',
  categories: ['ground_transport'],
  status: 'draft',
  canPublish: false,
  publicationLimit: '0',
  userLimit: '0',
  street: '',
  streetNumber: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  isVerified: false,
  owner: currentUserId,
});

const blankUser = {
  email: '',
  password: '',
  name: '',
  phoneNumber: '',
  role: 'provider',
  companyId: '',
};

const ADMIN_MENU_SECTIONS = [
  {
    label: 'Resumen',
    items: [
      {
        label: 'Resumen general',
        key: 'overview',
        icon: BarChartSquare02,
      },
    ],
  },
  {
    label: 'Operaciones',
    items: [
      {
        label: 'Empresas',
        key: 'companies',
        icon: Rows01,
      },
      {
        label: 'Usuarios',
        key: 'users',
        icon: Users01,
      },
    ],
  },
  {
    label: 'Soporte',
    items: [
      {
        label: 'Soporte',
        key: 'support',
        icon: MessageChatCircle,
      },
    ],
  },
];

export default function AdminDashboard({ user }) {
  const [activeTab, setActiveTab] = useState('companies');
  const [companyView, setCompanyView] = useState('list');
  const [userView, setUserView] = useState('list');

  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState('');
  const isEditingCompany = Boolean(editingCompanyId);
  const [companyFormTab, setCompanyFormTab] = useState('details');

  const [companyForm, setCompanyForm] = useState(blankCompany(user?.id));
  const [userForm, setUserForm] = useState(blankUser);
  const [documentForm, setDocumentForm] = useState({
    companyId: '',
    title: '',
    documentKey: '',
    file: null,
    notes: '',
  });
  const [documentFormKey, setDocumentFormKey] = useState(0);
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const [companyError, setCompanyError] = useState('');
  const [userError, setUserError] = useState('');
  const [documentsError, setDocumentsError] = useState('');
  const [flashMessage, setFlashMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Aceptar',
    cancelLabel: 'Cancelar',
    tone: 'primary',
    onConfirm: null,
  });

  const [pageVisitMetrics, setPageVisitMetrics] = useState({
    loading: false,
    summary: {},
    ranges: {},
    error: '',
    missingCollection: false,
  });

  const pageVisitDefinitions = useMemo(() => getPageVisitDefinitions(), []);
  const pageVisitTimeframes = useMemo(
    () => [
      { key: 'daily', label: '24h', description: PAGE_VISIT_TIMEFRAMES.daily.label },
      { key: 'weekly', label: '7 días', description: PAGE_VISIT_TIMEFRAMES.weekly.label },
      { key: 'monthly', label: '30 días', description: PAGE_VISIT_TIMEFRAMES.monthly.label },
      { key: 'yearly', label: '12 meses', description: PAGE_VISIT_TIMEFRAMES.yearly.label },
    ],
    []
  );
  const numberFormatter = useMemo(() => new Intl.NumberFormat('es-MX'), []);
  const pageVisitDailyBreakdown = useMemo(() => {
    const dailyEntries = pageVisitMetrics.summary?.daily;
    if (!dailyEntries) {
      return {
        total: 0,
        registered: 0,
        guests: 0,
        durationMsTotal: 0,
        registeredDurationMsTotal: 0,
        guestDurationMsTotal: 0,
        averageDurationMs: 0,
      };
    }
    const aggregate = Object.values(dailyEntries).reduce(
      (acc, entry) => {
        const total = Number(entry?.total) || 0;
        const registered = Number(entry?.registered) || 0;
        const guests = Number(entry?.guests) || 0;
        const durationMsTotal = Number(entry?.durationMsTotal) || 0;
        const registeredDurationMsTotal = Number(entry?.registeredDurationMsTotal) || 0;
        const guestDurationMsTotal = Number(entry?.guestDurationMsTotal) || 0;
        return {
          total: acc.total + total,
          registered: acc.registered + registered,
          guests: acc.guests + guests,
          durationMsTotal: acc.durationMsTotal + durationMsTotal,
          registeredDurationMsTotal:
            acc.registeredDurationMsTotal + registeredDurationMsTotal,
          guestDurationMsTotal: acc.guestDurationMsTotal + guestDurationMsTotal,
        };
      },
      {
        total: 0,
        registered: 0,
        guests: 0,
        durationMsTotal: 0,
        registeredDurationMsTotal: 0,
        guestDurationMsTotal: 0,
      }
    );
    const averageDurationMs = computeAverageDuration(aggregate.durationMsTotal, aggregate.total);
    const registeredAverageDurationMs = computeAverageDuration(
      aggregate.registeredDurationMsTotal,
      aggregate.registered
    );
    const guestAverageDurationMs = computeAverageDuration(
      aggregate.guestDurationMsTotal,
      aggregate.guests
    );
    return {
      ...aggregate,
      averageDurationMs,
      registeredAverageDurationMs,
      guestAverageDurationMs,
    };
  }, [pageVisitMetrics.summary]);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (user?.id) {
      setCompanyForm(prev => ({
        ...prev,
        owner: prev.owner || user.id,
      }));
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isAdmin) return;
    refreshCompanies();
    refreshUsers();
  }, [isAdmin]);

  useEffect(() => {
    if (activeTab === 'companies') {
      setCompanyView('list');
    } else if (activeTab === 'users') {
      setUserView('list');
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isAdmin || activeTab !== 'overview') return;

    let canceled = false;

    const loadPageVisitMetrics = async () => {
      setPageVisitMetrics(prev => ({
        ...prev,
        loading: true,
        error: '',
      }));

      try {
        const result = await fetchPageVisitSummary();
        if (canceled) return;
        setPageVisitMetrics({
          loading: false,
          summary: result?.summary || {},
          ranges: result?.ranges || {},
          error: result?.missingCollection
            ? 'La colección page_visits no está disponible. Crea la colección en PocketBase para habilitar el reporte.'
            : '',
          missingCollection: Boolean(result?.missingCollection),
        });
      } catch (error) {
        if (canceled) return;
        setPageVisitMetrics({
          loading: false,
          summary: {},
          ranges: {},
          error:
            resolveApiError(error) ||
            'No se pudieron cargar las visitas de páginas.',
          missingCollection: false,
        });
      }
    };

    loadPageVisitMetrics();

    return () => {
      canceled = true;
    };
  }, [isAdmin, activeTab]);

  const refreshCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const list = await fetchCompanies();
      setCompanies(list);
    } catch (error) {
      setCompanyError(resolveApiError(error));
    } finally {
      setLoadingCompanies(false);
    }
  };

  const refreshUsers = async () => {
    setLoadingUsers(true);
    try {
      const list = await fetchUsers();
      setUsers(list);
    } catch (error) {
      setUserError(resolveApiError(error));
    } finally {
      setLoadingUsers(false);
    }
  };

  const refreshCompanyDocuments = async companyId => {
    if (!companyId) {
      setCompanyDocuments([]);
      return;
    }
    setLoadingDocuments(true);
    try {
      const list = await fetchCompanyDocuments(companyId);
      setCompanyDocuments(list);
    } catch (error) {
      setDocumentsError(resolveApiError(error));
    } finally {
      setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    if (companyFormTab === 'documents' && isEditingCompany && editingCompanyId) {
      refreshCompanyDocuments(editingCompanyId);
    }
  }, [companyFormTab, isEditingCompany, editingCompanyId]);

  const stats = useMemo(() => {
    const published = companies.filter(c => c.status === 'published').length;
    const providers = users.filter(u => u.role === 'provider').length;
    const totalVisits = numberFormatter.format(pageVisitDailyBreakdown.total || 0);
    const registeredVisits = numberFormatter.format(pageVisitDailyBreakdown.registered || 0);
    const guestVisits = numberFormatter.format(pageVisitDailyBreakdown.guests || 0);
    const averageDurationLabel = formatDuration(pageVisitDailyBreakdown.averageDurationMs);
    return [
      {
        title: 'Empresas registradas',
        value: companies.length,
        detail: `${published} publicadas`,
      },
      {
        title: 'Usuarios totales',
        value: users.length,
        detail: `${providers} proveedores`,
      },
      {
        title: 'Visitas públicas (24h)',
        value: totalVisits,
        detail: `Registrados: ${registeredVisits} | Invitados: ${guestVisits} | Tiempo prom.: ${averageDurationLabel}`,
      },
    ];
  }, [companies, users, numberFormatter, pageVisitDailyBreakdown]);

  const sidebarAccountCard = useMemo(() => {
    if (!user) return null;
    const name = user.name?.trim() || 'Administrador';
    const email = user.email || '';
    return {
      name,
      email,
      planLabel: 'Admin',
      planColor: '#2563eb',
      statusLabel: 'Activo',
    };
  }, [user]);

  const companyOptions = useMemo(
    () =>
      companies.map(c => ({
        id: c.id,
        label: c.name || c.slug || c.id,
      })),
    [companies]
  );

  const userOptions = useMemo(
    () =>
      users.map(u => ({
        id: u.id,
        label: u.name || u.email || u.username || u.id,
      })),
    [users]
  );

  const normalizeSingleId = value =>
    Array.isArray(value) ? value[0] ?? '' : value ?? '';

  const usersById = useMemo(() => {
    const map = {};
    users.forEach(u => {
      map[u.id] = u;
    });
    return map;
  }, [users]);

  const companiesById = useMemo(() => {
    const map = {};
    companies.forEach(c => {
      map[c.id] = c;
    });
    return map;
  }, [companies]);

  const activeCompany = useMemo(
    () => (editingCompanyId ? companiesById[editingCompanyId] || null : null),
    [companiesById, editingCompanyId]
  );

  const companyFormHeadings = useMemo(() => {
    if (companyFormTab === 'documents') {
      return {
        title: 'Documentos de empresa',
        subtitle: isEditingCompany
          ? `Carga y administra archivos de ${activeCompany?.name || 'la empresa seleccionada'}.`
          : 'Guarda la empresa para habilitar la carga de documentos.',
      };
    }

    if (companyFormTab === 'settings') {
      return {
        title: 'ConfiguraciÃ³n de empresa',
        subtitle: 'Controla permisos, lÃ­mites y asignaciones para esta empresa.',
      };
    }

    return {
      title: isEditingCompany ? 'Editar empresa' : 'Crear empresa',
      subtitle: isEditingCompany
        ? 'Actualiza los datos de un proveedor o cliente existente.'
        : 'Publica nuevos proveedores o clientes dentro del marketplace.',
    };
  }, [companyFormTab, isEditingCompany, activeCompany?.name]);

  const resolveUserEmail = userLike => {
    if (!userLike) {
      return { label: 'Correo no disponible', isFallback: true };
    }

    const record =
      typeof userLike === 'string'
        ? usersById[userLike]
        : userLike?.id
        ? usersById[userLike.id] || userLike
        : userLike;

    if (!record) {
      return { label: 'Correo no disponible', isFallback: true };
    }

    const email = `${record.email || ''}`.trim();
    if (email) {
      return { label: email, isFallback: false };
    }

    const fallback =
      `${record.username || ''}`.trim() ||
      `${record.name || ''}`.trim() ||
      `${record.phoneNumber || ''}`.trim();

    if (fallback) {
      return { label: fallback, isFallback: true };
    }

    return { label: 'Correo no disponible', isFallback: true };
  };

  const resolveOwnerName = ownerId => {
    const id = normalizeSingleId(ownerId);
    if (!id) return '-';
    const owner = usersById[id];
    if (!owner) return '-';
    const name = `${owner.name || ''}`.trim();
    if (name) {
      return name;
    }
    return resolveUserEmail(owner).label;
  };

  const resolveCompanyName = companyId => {
    const id = normalizeSingleId(companyId);
    if (!id) return '-';
    const company = companiesById[id];
    return company?.name || company?.slug || '-';
  };

  const formatLimitValue = limit => {
    if (limit === undefined || limit === null || limit === '') {
      return 'Sin dato';
    }
    const numericValue = Number(limit);
    if (!Number.isFinite(numericValue)) {
      return '-';
    }
    if (numericValue <= 0) {
      return 'Sin limite';
    }
    return numericValue;
  };

  const generateSlug = value =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const resetCompanyFormState = () => {
    setCompanyForm(blankCompany(user?.id));
    setEditingCompanyId('');
    setCompanyFormTab('details');
    setCompanyDocuments([]);
    resetDocumentForm();
  };

  const resetDocumentForm = (companyId = '') => {
    setDocumentForm({
      companyId,
      title: '',
      documentKey: '',
      file: null,
      notes: '',
    });
    setDocumentsError('');
    setDocumentFormKey(prev => prev + 1);
  };

  const showCompanyList = () => {
    setCompanyView('list');
    setCompanyFormTab('details');
    setCompanyError('');
    setDocumentsError('');
  };

  const showCompanyCreate = () => {
    resetCompanyFormState();
    setCompanyView('create');
    setCompanyError('');
    setDocumentsError('');
  };

  const handleCompanyFormTabChange = tab => {
    if (tab === 'documents' && !isEditingCompany) {
      return;
    }
    setCompanyFormTab(tab);
    setCompanyError('');
    setDocumentsError('');
  };

  const handleResetCompanyForm = () => {
    const wasEditing = isEditingCompany;
    resetCompanyFormState();
    setCompanyError('');
    setDocumentsError('');
    if (wasEditing) {
      setCompanyView('list');
    }
  };

  const handleResetDocumentForm = () => {
    resetDocumentForm(editingCompanyId || '');
  };

  const showUserList = () => {
    setUserView('list');
    setUserError('');
  };

  const showUserCreate = () => {
    setUserView('create');
    setUserError('');
  };

  const handleResetUserForm = () => {
    setUserForm(blankUser);
    setUserError('');
  };

  const loadCompanyIntoForm = company => {
    const rawCategories = company.category;
    const categories = Array.isArray(rawCategories)
      ? rawCategories
      : rawCategories
      ? [rawCategories]
      : ['ground_transport'];

    setCompanyForm({
      name: company.name || '',
      slug: company.slug || '',
      categories,
      status: company.status || 'draft',
      canPublish: !!company.canPublish,
      publicationLimit:
        company.publicationLimit === undefined || company.publicationLimit === null
          ? ''
          : String(company.publicationLimit),
      userLimit:
        company.userLimit === undefined || company.userLimit === null
          ? ''
          : String(company.userLimit),
      street: company.street || '',
      streetNumber: company.streetNumber || '',
      city: company.city || '',
      state: company.state || '',
      country: company.country || '',
      postalCode: company.postalCode || '',
      isVerified: !!company.isVerified,
      owner: normalizeSingleId(company.owner) || '',
    });
    setEditingCompanyId(company.id || '');
    setCompanyFormTab('details');
    resetDocumentForm(company.id || '');
    if (company.id) {
      refreshCompanyDocuments(company.id);
    }
  };

  const handleCompanyChange = (field, value) => {
    setCompanyForm(prev => {
      if (field === 'categories') {
        const nextCategories = Array.isArray(value) ? value : [];
        return {
          ...prev,
          categories: nextCategories.length > 0 ? nextCategories : ['ground_transport'],
        };
      }

      if (field === 'publicationLimit' || field === 'userLimit') {
        if (value === '') {
          return { ...prev, [field]: '' };
        }
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) {
          return prev;
        }
        return {
          ...prev,
          [field]: String(Math.max(0, Math.floor(numeric))),
        };
      }

      if (field === 'name') {
        const next = { ...prev, name: value };
        if (!isEditingCompany) {
          next.slug = generateSlug(value);
        }
        return next;
      }

      return { ...prev, [field]: value };
    });
  };

  const handleEditCompany = company => {
    loadCompanyIntoForm(company);
    setCompanyError('');
    setDocumentsError('');
    setFlashMessage('');
    setCompanyView('create');
  };

  const handleDeleteCompany = async company => {
    const confirmation = window.confirm(
      `â”¬â”Eliminar la empresa "${company.name || company.slug || company.id}"? Esta acciÃ³n no se puede deshacer.`
    );
    if (!confirmation) return;

    setCompanyError('');
    setFlashMessage('');
    setLoadingCompanies(true);

    try {
      await deleteCompany(company.id);
      if (editingCompanyId === company.id) {
        resetCompanyFormState();
      }
      await refreshCompanies();
      setFlashMessage('Empresa eliminada exitosamente.');
      setCompanyView('list');
    } catch (error) {
      setCompanyError(resolveApiError(error));
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleDocumentFormChange = (field, value) => {
    setDocumentForm(prev => {
      if (field === 'file') {
        return { ...prev, file: value instanceof File ? value : null };
      }
      if (field === 'documentKey') {
        const next = { ...prev, documentKey: value };
        if (!prev.title || prev.title === COMPANY_DOCUMENT_MAP[prev.documentKey]?.label) {
          const definition = COMPANY_DOCUMENT_MAP[value];
          next.title = definition?.label || '';
        }
        return next;
      }
      return { ...prev, [field]: value };
    });
  };

  const handleDocumentSubmit = async event => {
    event.preventDefault();
    setDocumentsError('');
    setFlashMessage('');

    const companyId = documentForm.companyId || editingCompanyId;

    if (!companyId) {
      setDocumentsError('Selecciona una empresa para continuar.');
      return;
    }

    if (!documentForm.documentKey) {
      setDocumentsError('Selecciona el tipo de documento que deseas cargar.');
      return;
    }

    const trimmedTitle = documentForm.title?.trim();
    const definition = COMPANY_DOCUMENT_MAP[documentForm.documentKey];
    const resolvedTitle = trimmedTitle || definition?.label || documentForm.documentKey;

    if (!resolvedTitle) {
      setDocumentsError('Ingresa un titulo para el documento.');
      return;
    }

    if (!documentForm.file) {
      setDocumentsError('Selecciona un archivo para cargar.');
      return;
    }

    setLoadingDocuments(true);

    try {
      await createCompanyDocument({
        companyId,
        documentKey: documentForm.documentKey,
        title: resolvedTitle,
        file: documentForm.file,
        notes: documentForm.notes?.trim() || '',
      });

      await refreshCompanyDocuments(companyId);
      resetDocumentForm(companyId);
      setFlashMessage('Documento cargado correctamente.');
    } catch (error) {
      setDocumentsError(resolveApiError(error));
    } finally {
      setLoadingDocuments(false);
    }
  };


  const handleDeleteDocument = document => {
    if (!document) return;
    const label = document.title || document.file || document.id || 'este documento';
    setConfirmDialog({
      open: true,
      title: 'Eliminar documento',
      message: `Â¿Eliminar el documento "${label}"? Esta acción no se puede deshacer.`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
      onConfirm: () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        performDeleteDocument(document);
      },
    });
  };

  const performDeleteDocument = async document => {
    if (!document?.id) {
      return;
    }

    setDocumentsError('');
    setLoadingDocuments(true);

    const companyId = normalizeSingleId(document.company) || editingCompanyId;

    try {
      await deleteCompanyDocument(document.id);
      if (companyId) {
        await refreshCompanyDocuments(companyId);
      } else {
        setCompanyDocuments(prev => prev.filter(item => item.id !== document.id));
      }
      setFlashMessage('Documento eliminado correctamente.');
    } catch (error) {
      setDocumentsError(resolveApiError(error));
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleUpdateDocumentStatus = async (document, status) => {
    if (!document?.id) {
      return;
    }

    setDocumentsError('');
    setFlashMessage('');
    setLoadingDocuments(true);

    const companyId = normalizeSingleId(document.company) || editingCompanyId;

    try {
      await updateCompanyDocumentStatus(document.id, status);
      if (companyId) {
        await refreshCompanyDocuments(companyId);
      }
      let message = 'Documento actualizado.';
      if (status === COMPANY_DOCUMENT_STATUS.VERIFIED) {
        message = 'Documento verificado correctamente.';
      } else if (status === COMPANY_DOCUMENT_STATUS.PENDING) {
        message = 'Documento marcado como pendiente.';
      } else if (status === COMPANY_DOCUMENT_STATUS.REJECTED) {
        message = 'Documento marcado como rechazado.';
      }
      setFlashMessage(message);
    } catch (error) {
      setDocumentsError(resolveApiError(error));
    } finally {
      setLoadingDocuments(false);
    }
  };


  const handleUserChange = (field, value) => {
    setUserForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitCompany = async event => {
    event.preventDefault();
    setCompanyError('');
    setFlashMessage('');
    setLoadingCompanies(true);

    try {
      const payload = {
        name: companyForm.name.trim(),
        slug: companyForm.slug.trim(),
        status: companyForm.status,
        canPublish: companyForm.canPublish,
        isVerified: companyForm.isVerified,
        street: companyForm.street.trim(),
        streetNumber: companyForm.streetNumber.trim(),
        city: companyForm.city.trim(),
        state: companyForm.state.trim(),
        country: companyForm.country.trim(),
        postalCode: companyForm.postalCode.trim(),
      };

      const categories = Array.isArray(companyForm.categories)
        ? companyForm.categories.filter(Boolean)
        : [];

      if (categories.length > 0) {
        payload.category = categories;
      }

      const publicationLimit =
        companyForm.publicationLimit === ''
          ? undefined
          : Number(companyForm.publicationLimit);

      if (publicationLimit !== undefined && Number.isFinite(publicationLimit)) {
        payload.publicationLimit = publicationLimit;
      }

      const userLimit =
        companyForm.userLimit === '' ? undefined : Number(companyForm.userLimit);

      if (userLimit !== undefined && Number.isFinite(userLimit)) {
        payload.userLimit = userLimit;
      }

      if (companyForm.owner) payload.owner = companyForm.owner;

      const isEditing = Boolean(editingCompanyId);

      if (isEditing) {
        await updateCompany(editingCompanyId, payload);
      } else {
        await createCompany(payload);
      }

      await refreshCompanies();
      resetCompanyFormState();
      setCompanyError('');
      setFlashMessage(
        isEditing ? 'Empresa actualizada exitosamente.' : 'Empresa creada exitosamente.'
      );
      setActiveTab('companies');
      setCompanyView('list');
    } catch (error) {
      setCompanyError(resolveApiError(error));
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleSubmitUser = async event => {
    event.preventDefault();
    setUserError('');
    setFlashMessage('');

    if (!userForm.email || !userForm.password) {
      setUserError('Correo y contraseâ”œâ–’a son obligatorios.');
      return;
    }

    try {
      await createPocketbaseUser(userForm);
      await refreshUsers();
      setUserForm(blankUser);
      setFlashMessage('Usuario creado exitosamente.');
      setActiveTab('users');
      setUserView('list');
    } catch (error) {
      setUserError(resolveApiError(error));
    }
  };

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="dashboard-root admin-dashboard">
      <AdminSidebar
        sections={ADMIN_MENU_SECTIONS}
        activeKey={activeTab}
        onSelect={setActiveTab}
        heading={sidebarAccountCard?.name || 'Panel administrativo'}
        description={
          sidebarAccountCard?.email
            ? `Sesión de ${sidebarAccountCard.email}`
            : 'Gestiona la información clave de Flotte'
        }
      />

      <main className="dashboard-content admin-dashboard__content">
        <div className="admin-dashboard__canvas">
          {flashMessage && <div className="admin-floating-flash">{flashMessage}</div>}

          {activeTab === 'overview' && (
            <OverviewTab
              stats={stats}
              pageVisitDefinitions={pageVisitDefinitions}
              pageVisitTimeframes={pageVisitTimeframes}
              pageVisitSummary={pageVisitMetrics.summary}
              pageVisitRanges={pageVisitMetrics.ranges}
              pageVisitLoading={pageVisitMetrics.loading}
              pageVisitError={pageVisitMetrics.error}
            />
          )}

          {activeTab === 'companies' && (
            <CompaniesTab
              view={companyView}
              onShowList={showCompanyList}
              onShowCreate={showCompanyCreate}
              formTab={companyFormTab}
              onChangeFormTab={handleCompanyFormTabChange}
              isEditingCompany={isEditingCompany}
              headings={companyFormHeadings}
              companyForm={companyForm}
              companyError={companyError}
              documentsError={documentsError}
              loadingCompanies={loadingCompanies}
              loadingDocuments={loadingDocuments}
              companies={companies}
              companyDocuments={companyDocuments}
              userOptions={userOptions}
              onCompanyChange={handleCompanyChange}
              onSubmitCompany={handleSubmitCompany}
              onResetCompanyForm={handleResetCompanyForm}
              onEditCompany={handleEditCompany}
              onDeleteCompany={handleDeleteCompany}
              documentForm={documentForm}
              onDocumentChange={handleDocumentFormChange}
              onSubmitDocument={handleDocumentSubmit}
              onResetDocumentForm={handleResetDocumentForm}
              documentFormKey={documentFormKey}
              onDeleteDocument={handleDeleteDocument}
              onUpdateDocumentStatus={handleUpdateDocumentStatus}
              formatLimitValue={formatLimitValue}
              resolveOwnerName={resolveOwnerName}
              renderCategories={renderCategories}
              resolveDocumentUrl={getCompanyDocumentFileUrl}
            />
          )}

          {activeTab === 'users' && (
            <UsersTab
              view={userView}
              onShowList={showUserList}
              onShowCreate={showUserCreate}
              users={users}
              loadingUsers={loadingUsers}
              userError={userError}
              userForm={userForm}
              onUserChange={handleUserChange}
              onSubmitUser={handleSubmitUser}
              onResetUserForm={handleResetUserForm}
              companyOptions={companyOptions}
              resolveCompanyName={resolveCompanyName}
              resolveUserEmail={resolveUserEmail}
              roleLabel={roleLabel}
            />
          )}

          {activeTab === 'support' && (
            <SupportTab currentUser={user} companyOptions={companyOptions} />
          )}
        </div>
      </main>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel={confirmDialog.cancelLabel}
        tone={confirmDialog.tone}
        loading={loadingDocuments}
        onCancel={() =>
          setConfirmDialog(prev => ({
            ...prev,
            open: false,
          }))
        }
        onConfirm={confirmDialog.onConfirm}
      />
    </div>
  );
}

function resolveApiError(error) {
  if (!error) return 'Ocurrió un error inesperado.';
  if (typeof error === 'string') return error;
  const response = error?.response?.data || error?.data || {};
  const message =
    (typeof response.detail === 'string' && response.detail) ||
    (typeof response.message === 'string' && response.message) ||
    error?.message ||
    'Ocurrió un error inesperado.';
  return message;
}

function renderCategoryLabel(value) {
  switch (value) {
    case 'ground_transport':
      return 'Servicios terrestres';
    case 'security_service':
      return 'Custodia y seguridad';
    case 'suppliers':
      return 'Proveedores';
    case 'logistics_operator':
      return 'Operador logistico';
    case 'road_assistance':
      return 'Asistencia vial';
    case 'client':
      return 'Cliente';
    default:
      return 'Sin categoria';
  }
}

function renderCategories(value) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  if (list.length === 0) {
    return 'Sin categoria';
  }
  return list.map(renderCategoryLabel).join(', ');
}

function roleLabel(value) {
  switch (value) {
    case 'admin':
      return 'Administrador';
    case 'provider':
      return 'Proveedor';
    case 'client':
      return 'Cliente';
    default:
      return 'Sin rol';
  }
}

function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Aceptar',
  cancelLabel = 'Cancelar',
  tone = 'primary',
  loading = false,
  onCancel,
  onConfirm,
}) {
  if (!open) return null;

  const handleConfirm = () => {
    if (loading) return;
    if (typeof onConfirm === 'function') {
      onConfirm();
    }
  };

  return (
    <div className="admin-modal-backdrop" role="presentation">
      <div className="admin-modal" role="alertdialog" aria-modal="true">
        <header className="admin-modal-head">
          <h3>{title || 'Confirmar acción'}</h3>
        </header>
        <div className="admin-modal-body">
          <p>{message}</p>
        </div>
        <footer className="admin-modal-actions">
          <button
            type="button"
            className="admin-modal-button ghost"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`admin-modal-button ${tone}`}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Procesando...' : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
